import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, ListVideo, PlayCircle, Calendar } from "lucide-react";

const fontStack = { fontFamily: "'IBM Plex Sans', system-ui, sans-serif" };
const displayStack = { fontFamily: "'Outfit', system-ui, sans-serif" };

export default function WebinarSeriesList() {
  const [series, setSeries] = useState(null);

  useEffect(() => {
    api.get("/webinar-series").then(({ data }) => setSeries(data)).catch(() => setSeries([]));
  }, []);

  if (!series) return <div className="min-h-[40vh] flex items-center justify-center text-white/50 bg-[#050505]" style={fontStack}><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="bg-[#050505] text-white min-h-screen pb-20" style={fontStack} data-testid="webinar-series-page">
      <section className="border-b border-white/5 py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF0066]/15 border border-[#FF0066]/30 text-[#FF0066] text-xs uppercase tracking-wider font-semibold">
            <ListVideo className="w-3.5 h-3.5" /> Webinar Series
          </div>
          <h1 className="mt-4 text-3xl md:text-5xl font-extrabold" style={displayStack}>Curated learning series</h1>
          <p className="mt-3 text-white/70 max-w-2xl">Topic-wise webinar collections with live + replay sessions. Watch in order, build mastery faster.</p>
        </div>
      </section>

      <section className="py-10 px-4">
        <div className="max-w-6xl mx-auto space-y-10">
          {series.length === 0 && <div className="text-center text-white/50 py-12">No series yet. Check back soon!</div>}
          {series.map((s) => (
            <div key={s.id} className="rounded-2xl border border-white/10 bg-[#0a0a0a] overflow-hidden" data-testid={`series-${s.id}`}>
              <div className="grid md:grid-cols-3 gap-0">
                <div className="md:col-span-1 bg-gradient-to-br from-[#FF0066]/20 via-purple-500/10 to-transparent p-6 flex flex-col justify-between">
                  {s.cover_image && <img src={s.cover_image} alt={s.title} className="w-full aspect-video rounded-lg object-cover mb-4" />}
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold" style={displayStack}>{s.title}</h2>
                    {s.description && <p className="mt-2 text-sm text-white/70">{s.description}</p>}
                    <div className="mt-3 text-xs text-fuchsia-300 font-semibold">{(s.webinars || []).length} parts</div>
                  </div>
                </div>
                <div className="md:col-span-2 p-5 space-y-2">
                  {(s.webinars || []).length === 0 ? (
                    <div className="text-sm text-white/40 py-6 text-center">No webinars added to this series yet.</div>
                  ) : (
                    (s.webinars || []).map((w, i) => (
                      <Link key={w.id} to={`/webinar/${w.slug}`} className="flex items-center gap-3 rounded-lg border border-white/10 hover:border-[#FF0066]/40 hover:bg-white/[0.03] p-3 transition" data-testid={`series-webinar-${w.id}`}>
                        <div className="w-9 h-9 rounded-lg bg-[#FF0066]/15 flex items-center justify-center text-[#FF0066] text-sm font-bold shrink-0">{w.series_position || i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-white truncate">{w.title}</div>
                          <div className="text-[11px] text-white/50 mt-0.5 inline-flex items-center gap-3">
                            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(w.scheduled_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                            {w.replay_url && <span className="inline-flex items-center gap-1 text-emerald-300"><PlayCircle className="w-3 h-3" /> Replay</span>}
                            <span className="uppercase">{w.status}</span>
                          </div>
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
